import { CircleCheckIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import React, { useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native';

export interface SuccessDialogProps {
    visible: boolean;
    title: string;
    message?: string;
    /**
     * Optional auto-close delay in milliseconds.
     * After this delay elapses while visible, onOk will be called automatically.
     * Defaults to 3000ms.
     */
    autoCloseDelayMs?: number;
    onAutoClose: () => void;
}

export const SuccessDialog: React.FC<SuccessDialogProps> = ({
    visible,
    title,
    message,
    autoCloseDelayMs = 3000,
    onAutoClose,
}) => {
    const colors = useUIColors();
    const { t } = useTranslation();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const styles = StyleSheet.create({
        overlay: {
            flex: 1,
            backgroundColor: UI_PRIMITIVES.overlayBackdrop,
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: 24,
        },
        container: {
            backgroundColor: colors.white,
            borderRadius: 16,
            paddingVertical: 24,
            paddingHorizontal: 20,
            width: '100%',
            maxWidth: 224,
            alignItems: 'center',
            gap: 16,
        },
        iconContainer: {
            marginBottom: 0,
        },
        title: {
            textAlign: 'center',
            width: '100%',
        },
        message: {
            textAlign: 'center',
            width: '100%',
        },
    });

    // Auto-close behaviour
    useEffect(() => {
        if (!visible) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        // Start auto-close timer
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onAutoClose();
        }, autoCloseDelayMs);

        // Cleanup on unmount or when visibility/delay changes
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visible, autoCloseDelayMs, onAutoClose]);

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onAutoClose}>
            <View style={styles.overlay}>
                <View style={styles.container}>
                    <View style={styles.iconContainer}>
                        <CircleCheckIcon size={48} color={colors.fontPrimary} />
                    </View>
                    <Typography variant="title3" color="primary" style={styles.title}>
                        {title}
                    </Typography>
                    {!!message && (
                        <Typography variant="body" color="primary" style={styles.message}>
                            {message}
                        </Typography>
                    )}
                </View>
            </View>
        </Modal>
    );
};

export default SuccessDialog;

